import { useState, useEffect, useMemo, useCallback } from 'react';
import { ArrowLeft, Phone, Hash, DollarSign, Tag, FileText, Loader2, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import axios from 'axios';
import { toast } from 'sonner';
import DigitMaskPicker from './DigitMaskPicker';
import PriceModePicker from './PriceModePicker';
import usePhoneOperators, { assetUrl } from '../hooks/usePhoneOperators';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Bundled last-resort fallback (used only when the admin API returned no
// logo_asset_id AND the local operator id maps to a legacy image).
const FALLBACK_LOGO = {
  etisalat: '/carrier-etisalat.png',
  du: '/carrier-du.png',
};

export default function AddPhonePage({ store, language, isDarkMode, onBack, onSuccess, editItem = null, isEditMode = false }) {
  const [saving, setSaving] = useState(false);
  // iter-307 — load admin-maintained operator list
  const { operators: operatorsList } = usePhoneOperators();
  
  // Form state
  const [form, setForm] = useState(() => {
    if (editItem && isEditMode) {
      // Extract prefix and number from full number
      const fullNumber = editItem.number || '';
      const prefix = fullNumber.substring(0, 3) || '050';
      const number = fullNumber.substring(3) || '';
      
      return {
        operator: editItem.operator || 'etisalat',
        prefix: prefix,
        number: number,
        // Stored positions include the prefix — subtract prefix length for the UI
        hiddenDigits: (editItem.hiddenDigits || [])
          .map(i => i - prefix.length)
          .filter(i => i >= 0 && i < number.length),
        price: editItem.price || '',
        priceOnRequest: !!editItem.priceOnRequest,
        category: editItem.category || 'normal',
        paymentType: editItem.paymentType || editItem.planType || 'prepaid',
        hasContract: editItem.hasContract || editItem.contract || false,
        negotiable: editItem.negotiable || false,
        description: editItem.description || ''
      };
    }
    return {
      operator: 'etisalat',
      prefix: '050',
      number: '',
      hiddenDigits: [],
      price: '',
      priceOnRequest: false,
      category: 'normal',
      paymentType: 'prepaid',
      hasContract: false,
      negotiable: false,
      description: ''
    };
  });

  // iter-307 — Match the admin-side operator (by id, by "stub id" like
  // the one Etisalat uses, or by name). This keeps backward-compat with
  // the legacy string ids ("etisalat" / "du") stored on existing items.
  const activeOperator = useMemo(() => {
    const needle = (form.operator || '').toLowerCase();
    return operatorsList.find((op) => {
      const idLow = (op.id || '').toLowerCase();
      const enLow = (op.name_en || '').toLowerCase();
      return idLow === needle
        || enLow === needle
        || enLow.startsWith(needle)
        || (needle === 'etisalat' && enLow.includes('etisalat'))
        || (needle === 'du' && enLow === 'du');
    }) || operatorsList[0] || null;
  }, [form.operator, operatorsList]);

  // Codes come straight from admin now — fallback is the first operator's codes.
  const availablePrefixes = useMemo(
    () => activeOperator?.codes || [],
    [activeOperator]
  );

  useEffect(() => {
    if (!availablePrefixes.length) return;
    if (!availablePrefixes.includes(form.prefix)) {
      setForm(prev => ({ ...prev, prefix: availablePrefixes[0] }));
    }
  }, [availablePrefixes, form.prefix]);

  const update = useCallback((key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
  }, []);

  // Stable callback for DigitMaskPicker → avoids re-mounting on every keystroke
  const setHiddenDigits = useCallback((next) => {
    setForm(prev => ({ ...prev, hiddenDigits: next }));
  }, []);

  const handleSubmit = async () => {
    // Validation
    if (!form.number || form.number.length !== 7) {
      toast.error(language === 'ar' ? 'يرجى إدخال رقم هاتف صحيح (7 أرقام)' : 'Please enter a valid 7-digit phone number');
      return;
    }
    if (!form.priceOnRequest && (!form.price || parseFloat(form.price) <= 0)) {
      toast.error(language === 'ar' ? 'يرجى إدخال سعر صحيح أو اختيار "اتصل لمعرفة السعر"' : 'Please enter a valid price or choose "Call for price"');
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('business_token');
      
      // Use FormData for compatibility with backend
      const data = new FormData();
      data.append('storeType', 'phone_numbers');
      data.append('operator', form.operator);
      data.append('number', form.prefix + form.number);
      // iter-212: digit-mask indices are stored on the item so display code
      // can render the masked variant. Positions are relative to the full
      // number string (prefix + number), so shift by prefix length.
      const prefLen = (form.prefix || '').length;
      const shifted = (form.hiddenDigits || []).map(i => i + prefLen);
      data.append('hiddenDigits', JSON.stringify(shifted));
      data.append('price', form.priceOnRequest ? 0 : form.price);
      data.append('priceOnRequest', form.priceOnRequest ? 'true' : 'false');
      data.append('category', form.category);
      data.append('paymentType', form.paymentType);
      data.append('planType', form.paymentType);
      data.append('hasContract', form.hasContract);
      data.append('contract', form.hasContract);
      data.append('negotiable', form.negotiable);
      data.append('description', form.description || '');
      data.append('status', 'active');

      if (isEditMode && editItem) {
        // PUT request for update
        await axios.put(
          `${BACKEND_URL}/api/stores/${store.id}/items/${editItem.id}`,
          data,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(language === 'ar' ? 'تم تحديث الرقم بنجاح' : 'Number updated successfully');
      } else {
        // POST request for create
        await axios.post(
          `${BACKEND_URL}/api/stores/${store.id}/items`,
          data,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(language === 'ar' ? 'تم إضافة الرقم بنجاح' : 'Number added successfully');
      }
      
      onSuccess?.();
      onBack?.();
    } catch (error) {
      console.error('Error saving phone number:', error);
      toast.error(language === 'ar' ? 'حدث خطأ أثناء الحفظ' : 'Error saving phone number');
    } finally {
      setSaving(false);
    }
  };

  const cardCls = `rounded-2xl border ${isDarkMode ? 'bg-[#1E293B] border-white/10' : 'bg-white border-gray-200'}`;
  const labelCls = `block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`;
  const inputCls = `w-full px-3 py-2 border rounded-xl ${isDarkMode ? 'bg-[#0F172A] border-gray-600 text-white' : 'bg-white border-gray-300'}`;

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-[#0F172A]' : 'bg-gray-50'}`} dir={language === 'ar' ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className={`sticky top-0 z-10 ${isDarkMode ? 'bg-[#1E293B]' : 'bg-white'} shadow-sm`}>
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <button onClick={onBack} className="flex items-center gap-2 text-[#1E9BD9]">
            <ArrowLeft className="h-5 w-5" />
            <span>{language === 'ar' ? 'رجوع' : 'Back'}</span>
          </button>
          <h1 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
            {isEditMode 
              ? (language === 'ar' ? 'تعديل رقم الهاتف' : 'Edit Phone Number')
              : (language === 'ar' ? 'إضافة رقم هاتف جديد' : 'Add New Phone Number')
            }
          </h1>
          <div className="w-20" />
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-4">
        
        {/* Preview Card */}
        <div className={cardCls}>
          <div className="p-4">
            <h3 className={`font-bold mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
              <Phone className="h-5 w-5 text-[#1E9BD9]" />
              {language === 'ar' ? 'معاينة الرقم' : 'Number Preview'}
            </h3>
            
            <div className="flex items-center p-4 rounded-xl bg-gradient-to-br from-slate-800 to-slate-900 shadow-lg">
              <div className="flex-shrink-0">
                <img 
                  src={assetUrl(activeOperator?.logo_asset_id) || FALLBACK_LOGO[form.operator] || FALLBACK_LOGO.etisalat}
                  alt={activeOperator?.name_en || form.operator}
                  className="h-12 w-12 object-contain rounded-lg bg-white/10 p-1"
                  onError={(e) => { 
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = FALLBACK_LOGO[form.operator] || FALLBACK_LOGO.etisalat;
                  }}
                />
              </div>
              <div className="flex-1 text-center">
                <span className="font-bold text-2xl md:text-3xl tracking-widest text-white" dir="ltr">
                  {form.prefix}{form.number || '0000000'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Operator Selection — now driven by admin list (iter-307) */}
        <div className={cardCls}>
          <div className="p-4">
            <h3 className={`font-bold mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
              <Hash className="h-5 w-5 text-[#1E9BD9]" />
              {language === 'ar' ? 'مشغل الشبكة' : 'Network Operator'}
            </h3>
            
            <div className={`grid gap-3 ${operatorsList.length <= 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
              {operatorsList.map((op) => {
                const opKey = (op.id || op.name_en || '').toLowerCase().startsWith('op-')
                  ? op.name_en.toLowerCase()
                  : (op.id || op.name_en).toLowerCase();
                // Match active state against both the new id and legacy ids.
                const isActive =
                  activeOperator?.id === op.id
                  || form.operator === opKey
                  || form.operator === op.id
                  || (opKey.includes('etisalat') && form.operator === 'etisalat')
                  || (opKey === 'du' && form.operator === 'du');
                const simpleId = opKey.includes('etisalat')
                  ? 'etisalat'
                  : (opKey === 'du' ? 'du' : (opKey.includes('virgin') ? 'virgin' : opKey));
                return (
                  <button
                    key={op.id}
                    onClick={() => update('operator', simpleId)}
                    className={`p-4 rounded-xl border-2 flex flex-col items-center gap-2 transition-all ${
                      isActive
                        ? 'border-[#1E9BD9] bg-[#1E9BD9]/10'
                        : isDarkMode ? 'border-gray-600 hover:border-gray-500' : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid={`operator-btn-${simpleId}`}
                  >
                    <img 
                      src={assetUrl(op.logo_asset_id) || FALLBACK_LOGO[simpleId] || FALLBACK_LOGO.etisalat}
                      alt={op.name_en}
                      className="h-10 w-10 object-contain"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = FALLBACK_LOGO[simpleId] || FALLBACK_LOGO.etisalat;
                      }}
                    />
                    <span className={`font-medium text-sm text-center ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                      {language === 'ar' ? op.name_ar : op.name_en}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Phone Number */}
        <div className={cardCls}>
          <div className="p-4">
            <h3 className={`font-bold mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
              <Phone className="h-5 w-5 text-[#1E9BD9]" />
              {language === 'ar' ? 'رقم الهاتف' : 'Phone Number'}
            </h3>
            
            <div className="flex gap-2 justify-center items-stretch" dir="ltr">
              {/* Prefix — same visual weight as the number so both feel like one field */}
              <select
                value={form.prefix}
                onChange={(e) => update('prefix', e.target.value)}
                className={`w-24 h-16 px-2 border rounded-xl text-center text-2xl font-bold tracking-widest ${
                  isDarkMode ? 'bg-[#0F172A] border-gray-600 text-white' : 'bg-white border-gray-300'
                }`}
                data-testid="phone-prefix-select"
              >
                {availablePrefixes.map((prefix) => (
                  <option key={prefix} value={prefix}>{prefix}</option>
                ))}
              </select>

              {/* Number Input — matching height/font so both fields read as one number */}
              <Input
                type="tel" inputMode="numeric"
                value={form.number}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 7);
                  setForm(f => {
                    // Only shrink hiddenDigits if the number got shorter — keeps
                    // the array reference stable during normal typing.
                    const cur = f.hiddenDigits || [];
                    const trimmed = cur.length > 0 && cur.some(i => i >= val.length)
                      ? cur.filter(i => i < val.length)
                      : cur;
                    return { ...f, number: val, hiddenDigits: trimmed };
                  });
                }}
                placeholder="0000000"
                maxLength={7}
                className={`flex-1 h-16 text-2xl font-bold tracking-[0.3em] text-center ${
                  isDarkMode ? 'bg-[#0F172A] border-gray-600 text-white' : ''
                }`}
                data-testid="phone-number-input"
              />
            </div>
            <p className={`text-xs mt-2 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {language === 'ar' ? `أدخل 7 أرقام (${form.number.length}/7)` : `Enter 7 digits (${form.number.length}/7)`}
            </p>

            {/* iter-212: digit mask picker */}
            <DigitMaskPicker
              value={form.number}
              hiddenIndices={form.hiddenDigits}
              onChange={setHiddenDigits}
              kind="phone"
              language={language}
              isDarkMode={isDarkMode}
            />
          </div>
        </div>

        {/* Pricing */}
        <div className={cardCls}>
          <div className="p-4">
            <h3 className={`font-bold mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
              <DollarSign className="h-5 w-5 text-green-500" />
              {language === 'ar' ? 'السعر' : 'Pricing'}
            </h3>

            {/* iter-218: Price mode picker */}
            <PriceModePicker
              value={form.priceOnRequest ? 'onRequest' : 'fixed'}
              onChange={(m) => update('priceOnRequest', m === 'onRequest')}
              language={language}
              isDarkMode={isDarkMode}
              testIdPrefix="phone"
            />

            {!form.priceOnRequest && (
              <div className="relative mt-3">
                <Input
                  type="number" inputMode="decimal"
                  value={form.price}
                  onChange={(e) => update('price', e.target.value)}
                  placeholder="0"
                  className={`text-xl pe-16 ${isDarkMode ? 'bg-[#0F172A] border-gray-600 text-white' : ''}`}
                  data-testid="phone-price-input"
                />
                <div className="absolute inset-y-0 end-3 flex items-center pointer-events-none">
                  <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>AED</span>
                </div>
              </div>
            )}

            {/* Negotiable Toggle — remains usable even alongside "Contact for price"
                so merchants can advertise both signals to customers. */}
            <div className="mt-4 flex items-center justify-between">
              <Label className={isDarkMode ? 'text-gray-300' : ''}>
                {language === 'ar' ? 'قابل للتفاوض' : 'Negotiable'}
              </Label>
              <button
                type="button"
                onClick={() => update('negotiable', !form.negotiable)}
                dir="ltr"
                className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 ${
                  form.negotiable ? 'bg-[#1E9BD9]' : isDarkMode ? 'bg-gray-600' : 'bg-gray-300'
                }`}
                data-testid="phone-negotiable-toggle"
              >
                <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${
                  form.negotiable ? 'left-6' : 'left-0.5'
                }`} />
              </button>
            </div>
          </div>
        </div>

        {/* Category */}
        <div className={cardCls}>
          <div className="p-4">
            <h3 className={`font-bold mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
              <Tag className="h-5 w-5 text-amber-500" />
              {language === 'ar' ? 'الفئة' : 'Category'}
            </h3>
            
            <div className="grid grid-cols-4 gap-2">
              {[
                { value: 'vip', label: 'VIP', labelAr: 'VIP', color: 'bg-amber-500' },
                { value: 'golden', label: 'Golden', labelAr: 'ذهبي', color: 'bg-yellow-500' },
                { value: 'silver', label: 'Silver', labelAr: 'فضي', color: 'bg-gray-400' },
                { value: 'normal', label: 'Normal', labelAr: 'عادي', color: 'bg-blue-500' }
              ].map((cat) => (
                <button
                  key={cat.value}
                  onClick={() => update('category', cat.value)}
                  className={`p-3 rounded-xl border-2 text-center transition-all ${
                    form.category === cat.value
                      ? 'border-[#1E9BD9] bg-[#1E9BD9]/10'
                      : isDarkMode ? 'border-gray-600' : 'border-gray-200'
                  }`}
                >
                  <div className={`w-4 h-4 rounded-full ${cat.color} mx-auto mb-1`} />
                  <span className={`text-xs font-medium ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                    {language === 'ar' ? cat.labelAr : cat.label}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Plan Details */}
        <div className={cardCls}>
          <div className="p-4">
            <h3 className={`font-bold mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
              <FileText className="h-5 w-5 text-purple-500" />
              {language === 'ar' ? 'تفاصيل الباقة' : 'Plan Details'}
            </h3>
            
            {/* Payment Type */}
            <div className="mb-4">
              <Label className={`${labelCls} mb-2`}>{language === 'ar' ? 'نوع الفاتورة' : 'Billing Type'}</Label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { value: 'prepaid', label: 'Prepaid', labelAr: 'مسبق الدفع' },
                  { value: 'postpaid', label: 'Postpaid', labelAr: 'آجل الدفع' }
                ].map((type) => (
                  <button
                    key={type.value}
                    onClick={() => update('paymentType', type.value)}
                    className={`p-3 rounded-xl border-2 text-center transition-all ${
                      form.paymentType === type.value
                        ? 'border-[#1E9BD9] bg-[#1E9BD9]/10'
                        : isDarkMode ? 'border-gray-600' : 'border-gray-200'
                    }`}
                  >
                    <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                      {language === 'ar' ? type.labelAr : type.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Contract Toggle */}
            <div className="flex items-center justify-between">
              <Label className={isDarkMode ? 'text-gray-300' : ''}>
                {language === 'ar' ? 'مرتبط بعقد' : 'Has Contract'}
              </Label>
              <button
                type="button"
                onClick={() => update('hasContract', !form.hasContract)}
                dir="ltr"
                className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 ${
                  form.hasContract ? 'bg-[#1E9BD9]' : isDarkMode ? 'bg-gray-600' : 'bg-gray-300'
                }`}
                data-testid="phone-contract-toggle"
              >
                <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${
                  form.hasContract ? 'left-6' : 'left-0.5'
                }`} />
              </button>
            </div>
          </div>
        </div>

        {/* Description */}
        <div className={cardCls}>
          <div className="p-4">
            <Label className={labelCls}>{language === 'ar' ? 'ملاحظات (اختياري)' : 'Notes (Optional)'}</Label>
            <textarea
              value={form.description}
              onChange={(e) => update('description', e.target.value)}
              rows={3}
              placeholder={language === 'ar' ? 'أضف أي ملاحظات إضافية...' : 'Add any additional notes...'}
              className={`${inputCls} resize-none`}
            />
          </div>
        </div>

        {/* Submit — inline at end of form */}
        <div className="pt-2">
          <Button 
            onClick={handleSubmit} 
            disabled={saving || form.number.length !== 7 || (!form.priceOnRequest && !form.price)}
            className="w-full py-4 bg-[#1E9BD9] hover:bg-[#1E9BD9]/90 text-white rounded-xl text-lg font-bold"
            data-testid="phone-submit-btn"
          >
            {saving 
              ? <><Loader2 className="h-5 w-5 animate-spin me-2" />{language === 'ar' ? 'جاري الحفظ...' : 'Saving...'}</>
              : isEditMode 
                ? <><CheckCircle className="h-5 w-5 me-2" />{language === 'ar' ? 'حفظ التعديلات' : 'Save Changes'}</>
                : <><Phone className="h-5 w-5 me-2" />{language === 'ar' ? 'إضافة الرقم' : 'Add Number'}</>
            }
          </Button>
        </div>
      </div>
    </div>
  );
}
